import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAuthToken } from '@cdf/api-client-react';
import { ApiError } from '@cdf/api-client-react';

/**
 * Lightweight API helpers for the CV Training Studio.
 *
 * The generated Orval client does not yet include `/api/cv/*` endpoints,
 * so these use direct `fetch` calls with the same auth-token provider
 * registered by the MSAL lifecycle. Same-origin — no base URL needed.
 */

export type CelebrationEffect = 'fireworks_streamers' | 'confetti' | 'trophy' | 'none';

export const CELEBRATION_EFFECT_OPTIONS: ReadonlyArray<{ value: CelebrationEffect; label: string }> = [
  { value: 'fireworks_streamers', label: 'Fireworks & Streamers' },
  { value: 'confetti', label: 'Confetti Burst' },
  { value: 'trophy', label: 'Trophy Banner' },
  { value: 'none', label: 'None' },
];

export interface CvTarget {
  id: string;
  name: string;
  description: string | null;
  celebrationEffect: CelebrationEffect;
  filename: string;
  mimeType: string;
  descriptor: CvFeatureDescriptor | null;
  confidenceThreshold: number;
  huntStationId: string | null;
  huntChallengeId: string | null;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  descriptorSummary: {
    fileHash: string;
    width: number;
    height: number;
    sizeBytes: number;
    brightness: number;
    edgeScore: number;
    colorBuckets: number[];
  } | null;
}

export interface CvFeatureDescriptor {
  fileHash: string;
  sizeBytes: number;
  mimeType: string;
  width: number;
  height: number;
  colorBuckets: number[];
  brightness: number;
  edgeScore: number;
}

export interface CvTestResult {
  sample: {
    fileHash: string;
    sizeBytes: number;
    mimeType: string;
    width: number;
    height: number;
    brightness: number;
    edgeScore: number;
  };
  results: {
    targetId: string;
    targetName: string;
    score: number;
    threshold: number;
    matched: boolean;
  }[];
}

async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAuthToken();
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

async function jsonFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await authFetch(url, options);
  if (res.status === 204) return undefined as T;
  const isJson = (res.headers.get('content-type') ?? '').includes('application/json');
  if (!res.ok) {
    const body = isJson ? await res.json().catch(() => undefined) : undefined;
    throw new ApiError(
      body?.error ?? `Request failed with status ${res.status}`,
      res.status,
      body?.code
    );
  }
  return isJson ? (await res.json()) as T : undefined as T;
}

// ── Active target (public, no auth) ─────────────────────────────────────────

export interface ActiveCvTarget {
  id: string;
  name: string;
  celebrationEffect: CelebrationEffect;
  descriptor: CvFeatureDescriptor | null;
  confidenceThreshold: number;
  huntStationId: string | null;
  huntChallengeId: string | null;
  imageUrl: string;
}

export function useCvTargetsActive(huntSlug?: string | null) {
  const targetUrl = huntSlug
    ? `/api/cv/targets/active?huntSlug=${encodeURIComponent(huntSlug)}`
    : '/api/cv/targets/active';

  return useQuery({
    queryKey: ['cv-targets-active', huntSlug ?? null] as const,
    queryFn: () => jsonFetch<{ targets: ActiveCvTarget[]; total: number }>(targetUrl),
    staleTime: 30_000,
  });
}

export interface CvCaptureInput {
  targetId: string;
  challengeId: string;
  matchScore: number;
  source: 'camera' | 'upload';
  image?: { filename?: string; mimeType?: 'image/jpeg' | 'image/png' | 'image/webp'; sizeBytes?: number };
}

export interface CvCaptureResult {
  challengeId: string;
  targetId: string;
  targetName: string;
  pointsAwarded: number;
  totalPoints: number;
}

/** Submit a matched target using the attendee's short-lived hunt participant token. */
export async function submitCvCapture(
  huntSlug: string,
  participantToken: string,
  input: CvCaptureInput,
): Promise<CvCaptureResult> {
  const response = await fetch(`/api/hunts/${encodeURIComponent(huntSlug)}/captures`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${participantToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  const body = await response.json().catch(() => undefined) as { error?: string; code?: string } | CvCaptureResult | undefined;
  if (!response.ok) {
    const error = body as { error?: string; code?: string } | undefined;
    throw new ApiError(error?.error ?? 'Unable to verify this scan.', response.status, error?.code);
  }
  return body as CvCaptureResult;
}

// ── Query keys ──────────────────────────────────────────────────────────────

const cvTargetsKey = ['cv-targets'] as const;

// ── Hooks ───────────────────────────────────────────────────────────────────

export function useCvTargets() {
  return useQuery({
    queryKey: cvTargetsKey,
    queryFn: () => jsonFetch<{ targets: CvTarget[]; total: number }>('/api/cv/targets'),
  });
}

export function useCreateCvTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      file: File;
      name: string;
      description?: string;
      confidenceThreshold?: number;
      celebrationEffect?: CelebrationEffect;
    }) => {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('name', data.name);
      if (data.description) formData.append('description', data.description);
      if (data.confidenceThreshold !== undefined)
        formData.append('confidenceThreshold', String(data.confidenceThreshold));
      if (data.celebrationEffect) formData.append('celebrationEffect', data.celebrationEffect);
      return jsonFetch<CvTarget>('/api/cv/targets', { method: 'POST', body: formData });
    },
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: cvTargetsKey }),
      qc.invalidateQueries({ queryKey: ['cv-targets-active'] }),
    ]),
  });
}

export function useUpdateCvTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      id: string;
      name?: string;
      description?: string;
      confidenceThreshold?: number;
      celebrationEffect?: CelebrationEffect;
      huntStationId?: string | null;
      huntChallengeId?: string | null;
      isActive?: boolean;
    }) => {
      const { id, ...updates } = data;
      return jsonFetch<CvTarget>(`/api/cv/targets/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
    },
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: cvTargetsKey }),
      qc.invalidateQueries({ queryKey: ['cv-targets-active'] }),
    ]),
  });
}

export function useDeleteCvTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => jsonFetch<void>(`/api/cv/targets/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: cvTargetsKey }),
  });
}

export function useTrainCvTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      jsonFetch<CvTarget>(`/api/cv/targets/${id}/train`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: cvTargetsKey }),
  });
}

export function useTestCvSample() {
  return useMutation({
    mutationFn: async (data: { file: File; targetId?: string }) => {
      const formData = new FormData();
      formData.append('file', data.file);
      if (data.targetId) formData.append('targetId', data.targetId);
      return jsonFetch<CvTestResult>('/api/cv/test', { method: 'POST', body: formData });
    },
  });
}
